// Phase 7 of the weekly billing module — phleb-facing payslip history.
//
// Lists every weekly_phleb_payslips row belonging to the signed-in phleb.
// Per-row PDF download (signed URL, private bucket).
//
// Route: /phlebotomist/:id/payslips — linked from the PhlebDrawsView header.

import React, { useEffect, useState } from 'react';
import {
  Box, Container, Heading, Text, HStack, VStack, Flex, Icon, Button, Badge,
  Spinner, Alert, AlertIcon, Stat, StatLabel, StatNumber, StatHelpText,
  SimpleGrid, Table, Thead, Tbody, Tr, Th, Td, TableContainer, useToast,
} from '@chakra-ui/react';
import { FaArrowLeft, FaFilePdf, FaMoneyCheckAlt, FaCalendarWeek } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

const PDF_BUCKET = 'billing-pdfs';

function fmtMoney(n) {
  return Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
}

const STATUS_COLORS = { draft: 'gray', sent: 'blue', paid: 'green', void: 'red' };

export default function PhlebPayslipsPage() {
  const { id: phlebId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [payslips, setPayslips] = useState([]);
  const [phleb, setPhleb] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [{ data: rows }, { data: profile }] = await Promise.all([
        supabase
          .from('weekly_phleb_payslips')
          .select('id, week_ending, status, draws_count, gross_pay, adjustments_total, net_pay, sent_at, paid_at, paid_via, paid_reference, pdf_url, notes')
          .eq('phlebotomist_id', phlebId)
          .order('week_ending', { ascending: false }),
        supabase
          .from('phlebotomist_profiles')
          .select('id, full_name, email, company_name')
          .eq('id', phlebId)
          .maybeSingle(),
      ]);
      if (cancelled) return;
      setPayslips(rows || []);
      setPhleb(profile || null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [phlebId]);

  const openPdf = async (path) => {
    if (!path) {
      toast({ title: 'PDF not available yet', status: 'warning', duration: 3000 });
      return;
    }
    const { data, error } = await supabase.storage.from(PDF_BUCKET).createSignedUrl(path, 60 * 60);
    if (error || !data?.signedUrl) {
      toast({ title: 'Could not open PDF', description: error?.message, status: 'error', duration: 4000 });
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  };

  // Stats: lifetime + last-paid for a quick gut-check at the top.
  const stats = (() => {
    const visible = payslips.filter((p) => p.status !== 'void');
    const paid    = visible.filter((p) => p.status === 'paid');
    const unpaid  = visible.filter((p) => p.status !== 'paid');
    return {
      paidTotal:   paid.reduce((s, p) => s + Number(p.net_pay || 0), 0),
      unpaidTotal: unpaid.reduce((s, p) => s + Number(p.net_pay || 0), 0),
      count:       visible.length,
      lastPaidAt:  paid[0]?.paid_at || null,
    };
  })();

  return (
    <Container maxW="1100px" py={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <HStack>
          <Button size="sm" leftIcon={<FaArrowLeft />} variant="ghost" onClick={() => navigate(`/phlebotomist/${phlebId}`)}>
            Back to draws
          </Button>
        </HStack>
        <HStack>
          <Icon as={FaMoneyCheckAlt} color="purple.500" />
          <Heading size="lg">Payslips</Heading>
        </HStack>
      </Flex>

      {phleb && (
        <Text color="gray.600" mb={4}>
          Signed in as <b>{phleb.full_name}</b> ({phleb.email}). Payment via ACH to the bank account on file.
        </Text>
      )}

      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3} mb={6}>
        <Stat bg="white" p={4} borderRadius="md" borderWidth="1px">
          <StatLabel fontSize="xs">Paid lifetime</StatLabel>
          <StatNumber color="green.600">{fmtMoney(stats.paidTotal)}</StatNumber>
          <StatHelpText fontSize="xs">across {payslips.filter((p) => p.status === 'paid').length} payslips</StatHelpText>
        </Stat>
        <Stat bg="white" p={4} borderRadius="md" borderWidth="1px">
          <StatLabel fontSize="xs">Awaiting payment</StatLabel>
          <StatNumber color="orange.600">{fmtMoney(stats.unpaidTotal)}</StatNumber>
          <StatHelpText fontSize="xs">sent or draft</StatHelpText>
        </Stat>
        <Stat bg="white" p={4} borderRadius="md" borderWidth="1px">
          <StatLabel fontSize="xs">Payslip count</StatLabel>
          <StatNumber>{stats.count}</StatNumber>
          <StatHelpText fontSize="xs">excluding voided</StatHelpText>
        </Stat>
        <Stat bg="white" p={4} borderRadius="md" borderWidth="1px">
          <StatLabel fontSize="xs">Last paid</StatLabel>
          <StatNumber fontSize="lg">{fmtDate(stats.lastPaidAt)}</StatNumber>
          <StatHelpText fontSize="xs">most recent ACH</StatHelpText>
        </Stat>
      </SimpleGrid>

      {loading ? (
        <Flex justify="center" py={20}><Spinner size="lg" /></Flex>
      ) : payslips.length === 0 ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          No payslips yet. Once admin runs the Friday cycle for a week where you completed draws, the payslip will appear here.
        </Alert>
      ) : (
        <Box bg="white" borderRadius="md" borderWidth="1px" overflow="hidden">
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th><HStack spacing={1}><Icon as={FaCalendarWeek} /><Text>Week ending</Text></HStack></Th>
                  <Th>Status</Th>
                  <Th isNumeric>Draws</Th>
                  <Th isNumeric>Gross</Th>
                  <Th isNumeric>Adjust.</Th>
                  <Th isNumeric>Net pay</Th>
                  <Th>Sent</Th>
                  <Th>Paid</Th>
                  <Th>Method</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {payslips.map((p) => (
                  <Tr key={p.id} opacity={p.status === 'void' ? 0.5 : 1}>
                    <Td fontWeight="medium">{fmtDate(p.week_ending)}</Td>
                    <Td><Badge colorScheme={STATUS_COLORS[p.status] || 'gray'}>{p.status}</Badge></Td>
                    <Td isNumeric>{p.draws_count ?? '—'}</Td>
                    <Td isNumeric>{fmtMoney(p.gross_pay)}</Td>
                    <Td isNumeric color={Number(p.adjustments_total || 0) < 0 ? 'red.500' : 'gray.700'}>
                      {Number(p.adjustments_total || 0) === 0 ? '—' : fmtMoney(p.adjustments_total)}
                    </Td>
                    <Td isNumeric fontWeight="bold" color="purple.600">{fmtMoney(p.net_pay)}</Td>
                    <Td fontSize="xs" color="gray.600">{fmtDate(p.sent_at)}</Td>
                    <Td fontSize="xs" color="gray.600">{fmtDate(p.paid_at)}</Td>
                    <Td fontSize="xs" color="gray.600">{p.paid_via || '—'}</Td>
                    <Td>
                      {p.pdf_url && (
                        <Button size="xs" leftIcon={<FaFilePdf />} variant="outline" colorScheme="purple" onClick={() => openPdf(p.pdf_url)}>
                          PDF
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Container>
  );
}
